import React, { useRef, useEffect, useState } from 'react';
import { Element, Transforms } from 'slate';
import {
  ReactEditor,
  RenderElementProps,
  useSelected,
  useSlateStatic,
  useReadOnly,
} from 'slate-react';
import Katex from 'katex';
import { icons, Button, IconButton } from '@ublend-npm/aulaui-next';
import copy from 'copy-to-clipboard';
import { BlockToolbar } from '../BlockToolbar/BlockToolbar';

import {
  Container,
  Display,
  DisplayArea,
  SyntaxArea,
  LatexSyntax,
  EditorToggle,
  Error,
} from './Latex.styled';

export type LatexProps = {
  children: RenderElementProps['children'];
  expression: string;
  element: Element;
};

// Taken from TH90 implementation. No explanation given there but looks to allow
// unescaped spaces to be entered.
const cleanKatexString = (katexString: string) =>
  katexString.replace(/ /gi, '\\ ');

export const Latex = ({ expression, children, element }: LatexProps) => {
  const katexOutput = useRef() as React.MutableRefObject<HTMLDivElement>;
  const isReadOnly = useReadOnly();
  const [isCollapsed, setIsCollapsed] = useState(isReadOnly);
  const [hasError, setHasError] = useState(false);
  const [isHovering, setHovering] = useState(false);
  const editor = useSlateStatic();
  const isSelected = useSelected();

  useEffect(() => {
    try {
      Katex.render(cleanKatexString(expression), katexOutput.current, {
        displayMode: true,
      });
      setHasError(false);
    } catch (_) {
      setHasError(true);
    }
  }, [expression]);

  const toolbarItems = [
    {
      title: 'Copy',
      isVisible: true,
      icon: icons.Copy,
      onAction: () => copy(expression),
    },
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: icons.Delete,
      onAction: () => {
        const at = ReactEditor.findPath(editor, element);
        Transforms.removeNodes(editor, { at });
        if (editor.children.length === 0) {
          Transforms.insertNodes(editor, [
            {
              children: [{ text: '' }],
            },
          ]);
        }
        ReactEditor.focus(editor);
      },
    },
  ];

  const hoverToolbar = (
    <BlockToolbar isVisible={isSelected || isHovering} items={toolbarItems} />
  );

  return (
    <Container
      isSelected={isSelected}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {hoverToolbar}
      <DisplayArea contentEditable={false}>
        <Display ref={katexOutput} hasError={hasError} />
        <Error hasError={hasError}>
          <icons.WarningIcon />
          Invalid LaTeX
        </Error>
        {isReadOnly && (
          <EditorToggle>
            <Button
              onClick={() => setIsCollapsed(!isCollapsed)}
              type="text"
              size="small"
              iconRight={isCollapsed ? icons.ArrowDropDown : icons.ArrowDropUp}
            >
              {isCollapsed ? 'Show LaTeX' : 'Hide LaTeX'}
            </Button>
          </EditorToggle>
        )}
      </DisplayArea>
      <SyntaxArea hidden={isCollapsed}>
        <LatexSyntax
          data-testid="latex-syntax"
          onClick={() => ReactEditor.focus(editor)}
        >
          {children}
        </LatexSyntax>
        <div contentEditable={false}>
          <IconButton
            buttonAttributes={{
              role: 'link',
              tabIndex: isSelected ? 0 : -1,
              href: 'https://katex.org/docs/supported.html',
              target: '_blank',
            }}
            label="Help"
            type="secondary"
            size="xSmall"
            icon={icons.Help}
          />
        </div>
      </SyntaxArea>
    </Container>
  );
};
